import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";

import Model, { helper } from "@tripian/model";
import { PreLoading, Notification, FormTemplateTripNext } from "@tripian/react";
import { api } from "@tripian/core";

import * as gtag from "../../gtag";
import {
  CREATE_TRIP,
  USE_TRIP_URL,
  OVERVIEW,
  TRIP_PLAN,
} from "../../constants/ROUTER_PATH_TITLE";

import useTripForm from "../../hooks/useTripForm";
import useUser from "../../hooks/useUser";
import useCompanion from "../../hooks/useCompanion";
import useTrip from "../../hooks/useTrip";
import useTranslate from "../../hooks/useTranslate";
import AppNav from "../../App/AppNav/AppNav";
import {
  destinationTips,
  questionDefaultTip,
  travelerInfoTips,
  stepHeaders,
} from "../../config/tripFormContents";
import TripLoading from "../../components/TripLoading/TripLoading";
import classes from "./CreateUpdateTrip.module.scss";

// SessionStorage keys for error recovery
const SESSION_KEYS = {
  TRIP_PROFILE: "createTrip_tripProfile",
  CURRENT_STEP: "createTrip_currentStep",
};

// SessionStorage helpers
const saveToSession = (key: string, value: unknown) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.warn("Failed to save to sessionStorage:", e);
  }
};

const getFromSession = <T,>(key: string): T | null => {
  try {
    const item = sessionStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch (e) {
    console.warn("Failed to read from sessionStorage:", e);
    return null;
  }
};

const clearTripSession = () => {
  sessionStorage.removeItem(SESSION_KEYS.TRIP_PROFILE);
  sessionStorage.removeItem(SESSION_KEYS.CURRENT_STEP);
};

const emptyTripProfile: Model.TripProfile = {
  cityId: 0,
  arrivalDatetime: moment(new Date())
    .add(1, "days")
    .set({ hour: 9, minute: 0 })
    .format("YYYY-MM-DDTHH:mm:ss[Z]"),
  departureDatetime: moment(new Date())
    .add(4, "days")
    .set({ hour: 18, minute: 0 })
    .format("YYYY-MM-DDTHH:mm:ss[Z]"),
  answers: [],
  numberOfAdults: 1,
  // numberOfChildren: null,
  // pace: Model.TRIP_PROFILE_PACE.PACKED,
  // accommodation: null,
  companionIds: [],
  // owner: "",
  doNotGenerate: 0,
  excludeHash: [],
};

const CreateTripPage = () => {
  const {
    loadingCities,
    loadingQuestionsTrip,
    loadingQuestionsCompanion,
    cities,
    questionsTrip,
    questionsCompanion,
  } = useTripForm();
  // SessionStorage'dan kayıtlı değerleri oku (error recovery için)
  const savedTripProfile = getFromSession<Model.TripProfile>(SESSION_KEYS.TRIP_PROFILE);
  const savedStep = getFromSession<number>(SESSION_KEYS.CURRENT_STEP);

  const [tripProfile, setTripProfile] = useState<Model.TripProfile>(
    savedTripProfile || {
      ...emptyTripProfile,
      cityId: window.tconfig.DEFAULT_DESTINATION_ID ?? 0,
    }
  );
  const [currentStep, setCurrentStep] = useState<number | undefined>(savedStep ?? undefined);
  const [showWarningMessage, setShowWarningMessage] = useState<number>(0);
  const [suggestedCities, setSuggestedCities] = useState<any[]>([]);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const tripSubmittedRef = useRef(false);
  const [localIsSearching, setLocalIsSearching] = useState(false);

  const { t } = useTranslate();

  const { user } = useUser();
  const { tripFetchCallback } = useTrip();
  const { companions, companionAdd, loadingCompanions, loadingCompanionAdd } =
    useCompanion();
  const { tripAdd, loadingTripAdd, tripClear } = useTrip();

  const navigate = useNavigate();
  const location = useLocation();

  document.title = CREATE_TRIP.TITLE(t("trips.createNewTrip.header"));

  useEffect(() => {
    const redirectToUseTripUrl = () => {
      navigate(USE_TRIP_URL.PATH, { replace: true });
    };

    if (!window.tconfig.SHOW_CREATE_TRIP && window.tconfig.LOGIN_WITH_HASH) {
      redirectToUseTripUrl();
    }
  }, [navigate]);

  useEffect(() => {
    if (user) {
      // SessionStorage'da kayıtlı tripProfile varsa onu koru, sadece user.answers'ı ekle
      const savedProfile = getFromSession<Model.TripProfile>(SESSION_KEYS.TRIP_PROFILE);
      if (savedProfile) {
        // Kayıtlı profil var - sadece answers'ı güncelle (user answers ile birleştir)
        const mergedAnswers = Array.from(new Set([...(savedProfile.answers || []), ...(user.answers || [])]));
        setTripProfile({ ...savedProfile, answers: mergedAnswers });
      } else {
        // Kayıtlı profil yok - yeni profil oluştur
        const userEmptyTripProfile = {
          ...emptyTripProfile,
          cityId: window.tconfig.DEFAULT_DESTINATION_ID ?? 0,
          answers: [...emptyTripProfile.answers].concat(user.answers || []),
        };
        setTripProfile(userEmptyTripProfile);
      }
    }
  }, [user]);

  useEffect(() => {
    tripClear();
  }, [tripClear]);

  useEffect(() => {
    const fromSimplyCaribbean = location.state?.fromSimplyCaribbean;
    if (fromSimplyCaribbean) {
      const { cityId, arrivalDate, departureDate } = location.state;

      if (cityId && arrivalDate && departureDate) {
        setTripProfile(prevProfile => ({
          ...prevProfile,
          cityId: cityId,
          arrivalDatetime: moment(arrivalDate)
            .set({ hour: 9, minute: 0 })
            .format("YYYY-MM-DDTHH:mm:ss[Z]"),
          departureDatetime: moment(departureDate)
            .set({ hour: 18, minute: 0 })
            .format("YYYY-MM-DDTHH:mm:ss[Z]"),
        }));
      }
    }
  }, [location.state]);

  // Handler for AI city suggestions search
  const handleSearchChange = (text: string, isQuickSearch = false) => {
    // Clear previous timeout to avoid multiple calls
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (text.trim().length < 2 && !isQuickSearch) {
      return;
    }

    // Set a timeout to debounce the search
    searchTimeoutRef.current = setTimeout(
      () => {
        performSearch(text);
      },
      isQuickSearch ? 0 : 500
    ); // No delay for quick search buttons
  };

  // Function to call the API for city suggestions
  const performSearch = async (text: string) => {
    try {
      // Update the ref immediately
      setLocalIsSearching(true);

      // Clear previous suggestions to ensure loading state is shown
      setSuggestedCities([]);

      const suggestions = await api.suggestCity(text);

      setSuggestedCities(suggestions);
    } catch (error) {
      console.error("Error fetching city suggestions:", error);
      // Also set searching to false in case of error
        setLocalIsSearching(false);
    } finally {
        setLocalIsSearching(false); // Component re-render olur
    }
  };

  // Handler for selecting a suggested city
  const handleSuggestedCitySelect = (cityId: number) => {
    // Find the city in the list of cities
    const city = cities?.find(city => city.id === cityId);
    if (city) {
      // Update trip profile with selected city
      const updatedTripProfile = { ...tripProfile, cityId: city.id };
      setTripProfile(updatedTripProfile);
      callbackTripProfile(updatedTripProfile);

      // Clear suggestions after selection
      setSuggestedCities([]);
    }
  };

  const checkWarning = (tripProfileParam: Model.TripProfile) => {
    if (showWarningMessage === 0) {
      if (
        tripProfileParam.companionIds.length >
        tripProfileParam.numberOfAdults +
          (tripProfileParam.numberOfChildren || 0) -
          1
      ) {
        setShowWarningMessage(1);
      }
    }
  };

  const callbackTripProfile = (tripProfileParam: Model.TripProfile) => {
    setTripProfile(tripProfileParam);
    saveToSession(SESSION_KEYS.TRIP_PROFILE, tripProfileParam);
    checkWarning(tripProfileParam);
  };

  const handleStepChange = (stepId: number) => {
    setCurrentStep(stepId);
    if (!tripSubmittedRef.current) {
      saveToSession(SESSION_KEYS.CURRENT_STEP, stepId);
    }
  };

  const onTripAdd = () => {
    if (tripProfile) {
      tripAdd(tripProfile, 0).then((trip: Model.Trip) => {
        tripSubmittedRef.current = true;
        clearTripSession();
        tripFetchCallback(trip);
        if (gtag) {
          gtag.googleAnalyticsEvent("CreateTrip", { trip_hash: trip.tripHash });
        }
        setTimeout(() => {
          if (!window.tconfig.SHOW_OVERVIEW)
            navigate(`${TRIP_PLAN.PATH}/${trip.tripHash}`);
          else
            navigate(`${OVERVIEW.PATH}/${trip.tripHash}`);
        }, 100);
      });
    }
  };

  const tripCancel = () => {
    clearTripSession(); // Cancel edildi, session temizle
    const fromSimplyCaribbean = location.state?.fromSimplyCaribbean;
    if (fromSimplyCaribbean) {
      navigate("/landing", {
        state: {
          cityId: tripProfile.cityId,
          arrivalDate: tripProfile.arrivalDatetime,
          departureDate: tripProfile.departureDatetime,
        },
      });
    } else {
      navigate(-1);
    }
  };

  const callbackUserCompanionAdd = async (companion: Model.Companion) => {
    companionAdd(companion as Model.CompanionRequest).then(
      (companionAddResponse: Model.Companion[]) => {
        const companionIds = companions?.map(c => c.id);
        const companionId = companionAddResponse.find(
          c => !companionIds?.includes(c.id)
        )?.id;
        const newTripProfile = helper.deepCopy(tripProfile);
        if (companionId) {
          newTripProfile.companionIds.push(companionId);
          setTripProfile(newTripProfile);
        }
      }
    );
  };

  // const isButtonDisabled = !tripProfile.cityId || moment(tripProfile.arrivalDatetime).utcOffset(0) < moment().utcOffset(0);

  if (
    loadingCities ||
    loadingQuestionsTrip ||
    loadingQuestionsCompanion ||
    loadingCompanions ||
    questionsTrip === undefined
  )
    return (
      <>
        <AppNav
          header={CREATE_TRIP.HEADER?.(t("trips.createNewTrip.header"))}
        />
        <PreLoading bgColor="rgba(238, 238, 238, 0.8)" />;
      </>
    );

  return (
    <>
      <AppNav header={CREATE_TRIP.HEADER?.(t("trips.createNewTrip.header"))} />
      <div
        className="background"
        style={
          window.tconfig.IMAGE_PATHS.APP_BACKGROUND_IMG_URL
            ? {
                background: `url(${window.tconfig.IMAGE_PATHS.APP_BACKGROUND_IMG_URL})`,
              }
            : {}
        }
      >
        {loadingTripAdd ? (
          <div className={classes.createUpdateTripLoading}>
            <TripLoading t={t} />
          </div>
        ) : (
          <>
            {tripProfile ? (
              <div className={classes.formTemplateTripNextContainer}>
                <FormTemplateTripNext
                  cities={cities || []}
                  tripProfile={tripProfile}
                  tripQuestions={questionsTrip}
                  callbackTripProfile={callbackTripProfile}
                  userCompanionQuestions={questionsCompanion || []}
                  loadingCompanion={loadingCompanionAdd}
                  callbackUserCompanionAdd={callbackUserCompanionAdd}
                  userCompanions={companions}
                  showTripModeQuestion={window.tconfig.SHOW_TRIP_MODE_QUESTION}
                  stepHeaders={stepHeaders([
                    t("trips.createNewTrip.stepHeaders.destination"),
                    t("trips.createNewTrip.stepHeaders.travelerInfo"),
                    t("trips.createNewTrip.stepHeaders.itineraryProfile"),
                    t("trips.createNewTrip.stepHeaders.personalInterests"),
                  ])}
                  destinationTips={destinationTips([
                    {
                      title: t(
                        "trips.createNewTrip.questionDefaultTip.hover.title"
                      ),
                      description: t(
                        "trips.createNewTrip.questionDefaultTip.hover.description"
                      ),
                    },
                    {
                      title: t(
                        "trips.createNewTrip.destinationTips.destination.title"
                      ),
                      description: t(
                        "trips.createNewTrip.destinationTips.destination.description"
                      ),
                    },
                    {
                      title: t(
                        "trips.createNewTrip.destinationTips.arrivalDate.title"
                      ),
                      description: t(
                        "trips.createNewTrip.destinationTips.arrivalDate.description"
                      ),
                    },
                    {
                      title: t(
                        "trips.createNewTrip.destinationTips.departureDate.title"
                      ),
                      description: t(
                        "trips.createNewTrip.destinationTips.departureDate.description"
                      ),
                    },
                    {
                      title: t(
                        "trips.createNewTrip.destinationTips.arrivalHours.title"
                      ),
                      description: t(
                        "trips.createNewTrip.destinationTips.arrivalHours.description"
                      ),
                    },
                    {
                      title: t(
                        "trips.createNewTrip.destinationTips.departureHours.title"
                      ),
                      description: t(
                        "trips.createNewTrip.destinationTips.departureHours.description"
                      ),
                    },
                  ])}
                  travelerInfoTips={travelerInfoTips([
                    {
                      title: t(
                        "trips.createNewTrip.questionDefaultTip.hover.title"
                      ),
                      description: t(
                        "trips.createNewTrip.questionDefaultTip.hover.description"
                      ),
                    },
                    {
                      title: t(
                        "trips.createNewTrip.travelerInfoTips.travelers.title"
                      ),
                      description: t(
                        "trips.createNewTrip.travelerInfoTips.travelers.description"
                      ),
                    },
                    {
                      title: t(
                        "trips.createNewTrip.travelerInfoTips.children.title"
                      ),
                      description: t(
                        "trips.createNewTrip.travelerInfoTips.children.description"
                      ),
                    },
                    {
                      title: t(
                        "trips.createNewTrip.travelerInfoTips.startingPoint.title"
                      ),
                      description: t(
                        "trips.createNewTrip.travelerInfoTips.startingPoint.description"
                      ),
                    },
                    {
                      title: t(
                        "trips.createNewTrip.travelerInfoTips.companions.title"
                      ),
                      description: t(
                        "trips.createNewTrip.travelerInfoTips.companions.description"
                      ),
                    },
                  ])}
                  questionDefaultTip={questionDefaultTip({
                    title: t(
                      "trips.createNewTrip.questionDefaultTip.hover.title"
                    ),
                    description: t(
                      "trips.createNewTrip.questionDefaultTip.hover.description"
                    ),
                  })}
                  initialStep={
                    location.state?.fromSimplyCaribbean ? 0 : undefined
                  }
                  // @ts-ignore - startStep ve onStepChange yeni eklendi, paket rebuild gerekli
                  startStep={currentStep}
                  onStepChange={handleStepChange}
                  onSubmitText={t("trips.createNewTrip.form.submit")}
                  onSubmit={onTripAdd}
                  onCancel={tripCancel}
                  t={t}
                  // @ts-ignore - AI suggestion props
                  showAiSuggestions={Boolean(window.tconfig.AI_SUPPORT)}
                  citySuggestions={suggestedCities}
                  onCitySuggestionsSearch={handleSearchChange}
                  isCitySearching={localIsSearching}
                  onSelectSuggestedCity={handleSuggestedCitySelect}
                  onClearCitySuggestions={() => setSuggestedCities([])}
                />
                {showWarningMessage === 1 ? (
                  <Notification
                    type="warning"
                    positionX="center"
                    positionY="top"
                    title={t("trips.createNewTrip.warning.title")}
                    message={t("trips.createNewTrip.warning.message")}
                    onClose={() => {
                      setShowWarningMessage(2);
                    }}
                    closeMs={3500}
                  />
                ) : null}
              </div>
            ) : null}
          </>
        )}
      </div>
    </>
  );
};

export default CreateTripPage;
